import React, { useState } from 'react';

import {
  type FTemplateSectionFragment,
  useDeleteTemplateSectionMutation,
  useUpdateTemplateSectionMutation,
} from '@dashboard/generated/wp/platform';
import { DeleteOutlineOutlined, EditOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';

import { alertError, alertSuccess } from '@waypoint/react/notifications';

import { useCurrentTemplateSections, useCurrentTemplatesQuery } from './hooks';
import SectionLabelDialog from './SectionLabelDialog';

type RenameSectionButtonProps = {
  name: string;
  section: FTemplateSectionFragment;
  onOpenChange: (open: boolean) => void;
};
export function RenameSectionButton({ section, onOpenChange }: RenameSectionButtonProps) {
  const [open, setOpen] = useState(false);
  const [updateSection, { loading }] = useUpdateTemplateSectionMutation({
    optimisticResponse: (variables) => ({ updateTemplateSection: { ...section, name: variables.name } }),
  });

  const handleOpen = () => {
    setOpen(true);
    onOpenChange(true);
  };

  const handleClose = () => {
    setOpen(false);
    onOpenChange(false);
  };

  const renderDialog = () => {
    if (!open) {
      return null;
    }
    return (
      <SectionLabelDialog
        title="Rename section"
        submitLabel="Rename"
        initialValue={section.name}
        disabled={loading}
        onClose={handleClose}
        onSubmit={async (name: string) => {
          try {
            await updateSection({ variables: { id: section.id, name } });
            handleClose();
            alertSuccess('Section renamed');
          } catch (e) {
            alertError('Failed to rename section');
          }
        }}
      />
    );
  };

  return (
    <>
      <Tooltip title="Rename section">
        <IconButton
          size="small"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleOpen}
          sx={{
            color: 'text.secondary',
            opacity: 0.6,
            '&:hover': { opacity: 1, color: 'text.primary' },
          }}
        >
          <EditOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      {renderDialog()}
    </>
  );
}

type DeleteSectionButtonProps = {
  section: FTemplateSectionFragment;
  onOpenChange: (open: boolean) => void;
};
export function DeleteSectionButton({ section, onOpenChange }: DeleteSectionButtonProps) {
  const { refetch: refetchSections } = useCurrentTemplateSections();
  const { refetch: refetchTemplates } = useCurrentTemplatesQuery();
  const [open, setOpen] = useState(false);
  const [deleteSection] = useDeleteTemplateSectionMutation();
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    onOpenChange(true);
  };

  const handleClose = () => {
    setOpen(false);
    onOpenChange(false);
  };

  return (
    <>
      <Tooltip title="Remove section">
        <IconButton
          size="small"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={handleOpen}
          sx={{
            color: 'text.secondary',
            opacity: 0.6,
            '&:hover': { opacity: 1, color: 'error.main' },
          }}
        >
          <DeleteOutlineOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={loading ? undefined : handleClose}>
        <DialogTitle>Remove section?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Removing this section{' '}
            <Box component="strong" sx={{ fontWeight: 'bold' }}>
              will NOT delete any templates
            </Box>
            . They'll simply be ungrouped.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={loading}
            variant="contained"
            color="error"
            onClick={async () => {
              try {
                setLoading(true);
                await deleteSection({ variables: { id: section.id } });
                await Promise.all([refetchTemplates(), refetchSections()]);
                setLoading(false);
                alertSuccess('Section deleted');
                handleClose();
              } catch {
                setLoading(false);
                alertError('Failed to delete section');
              }
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
